// Auto-generated. Do not edit by hand.
// Source: data/kpis_kris/kpi_kri_catalog.csv
// Generator: data/kpis_kris/export_kpi_catalog_to_app.py
// Re-run the generator to refresh.

export interface KpiCatalogEntry {
  id: string;
  name: string;
  type: "KPI" | "KRI";
  category: string;
  summary: string;
  formula: string;
  where: string;
  why: string;
  how: string;
  impact: "Critical" | "High" | "Medium" | "Low";
  owner: string;
  refresh: string;
}

export const KPI_CATALOG: KpiCatalogEntry[] = [
  {
    "id": "K-001",
    "name": "Aggregate Over/Underbilling Position",
    "type": "KPI",
    "category": "Billing",
    "summary": "Aggregate over- or underbilling across the active portfolio in dollars.",
    "formula": "Σ billings_to_date − Σ earned_revenue_to_date · earned = pct_complete × current_contract_value",
    "where": "Overview · Portfolio Billing",
    "why": "Headline cash-position lever; documented $300M swings on this metric drove the engagement.",
    "how": "CFO and controller act on persistent drift; trigger pay-app review and EAC refresh on outliers.",
    "impact": "Critical",
    "owner": "CFO/Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-002",
    "name": "Aggregate Over/Underbilling Forecast (Quarterly)",
    "type": "KPI",
    "category": "Billing/Forecast",
    "summary": "Quarterly forward forecast of K-001 with conformal prediction intervals.",
    "formula": "Hierarchical reconciliation of per-project K-001 forecasts with 50/80/95 conformal PI",
    "where": "Portfolio Billing · Simulator",
    "why": "POC mandates ±10% accuracy with 80% PI coverage; this is the core forecast deliverable.",
    "how": "CFO uses for cash-line planning; Treasury sizes credit facility against the lower PI bound.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-003",
    "name": "Project-Type Mix Shift Index",
    "type": "KRI",
    "category": "Mix",
    "summary": "How much the portfolio's project-type composition shifted over a rolling 12 months.",
    "formula": "½ × Σ |share_t − share_t-12| over project types",
    "where": "Portfolio Billing · NIF · Simulator",
    "why": "Mix shift (e.g., clean-room 35%→7%) changes cash dynamics independently of performance; must separate.",
    "how": "Strategy + CFO review monthly to distinguish 'world changed' from 'we did worse on the same work'.",
    "impact": "Critical",
    "owner": "CFO/Strategy",
    "refresh": "Daily"
  },
  {
    "id": "K-004",
    "name": "Customer-Tier Mix Shift Index",
    "type": "KRI",
    "category": "Mix",
    "summary": "Same composition-shift index applied to customer-tier mix.",
    "formula": "½ × Σ |share_t − share_t-12| over customer tiers",
    "where": "AR & Customer Risk · NIF · Simulator",
    "why": "Tier mix drives AR cycle; brief lists tier mix as primary cash-cycle problem.",
    "how": "Sales leadership tracks; triggers review of tier-pricing and credit-terms policy when tier-shift exceeds threshold.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-005",
    "name": "Mix-Cash Impact ($)",
    "type": "KPI",
    "category": "Mix/Cash",
    "summary": "Counterfactual: cash impact of holding prior-period mix at current volume.",
    "formula": "(Σ revenue_at_prior_mix − Σ revenue_at_current_mix) × CCR",
    "where": "Portfolio Billing",
    "why": "Quantifies the dollar effect of mix shift in isolation, separating it from performance change.",
    "how": "CFO uses to decompose cash variance reports for the board into mix vs performance drivers.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-005b",
    "name": "Billing Cadence Drift",
    "type": "KRI",
    "category": "Billing",
    "summary": "Days from cost post to billing submit, drift versus 12-month archetype baseline.",
    "formula": "mean(days(cost_post_date → billing_submit_date)) per archetype − trailing-12 baseline",
    "where": "Portfolio Billing",
    "why": "Front-loaded SOV or billing slowdown both surface here before they hit DSO.",
    "how": "Operations triages outliers; controller resequences pay-app prep on slow-billing projects.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-006",
    "name": "Underbilling % of Revenue",
    "type": "KPI",
    "category": "Billing",
    "summary": "Underbilling expressed as a percentage of trailing-12 revenue.",
    "formula": "(Σ underbilled $) / (trailing-12 revenue)",
    "where": "Portfolio Billing",
    "why": "Normalises K-001 to size of business; comparable across periods and segments.",
    "how": "Used for trend reporting and benchmarking; shows whether underbilling is growing faster than revenue.",
    "impact": "Critical",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-006b",
    "name": "Cost-No-Billing Project Count",
    "type": "KRI",
    "category": "Billing",
    "summary": "Count of projects with cost activity but zero billing in the period.",
    "formula": "count(projects WHERE cost_period > 0 AND billing_period = 0)",
    "where": "Portfolio Billing",
    "why": "Hard signal of stalled billing; cost is being incurred but no claim is being made.",
    "how": "Controller calls each PM weekly to investigate; common causes are CO disputes or pay-app blocks.",
    "impact": "High",
    "owner": "Branch leads",
    "refresh": "Daily"
  },
  {
    "id": "K-007",
    "name": "High-Risk Billing Flag Count",
    "type": "KRI",
    "category": "Billing",
    "summary": "Count of billings flagged as high-risk by tax setup, missing CO, or threshold rules.",
    "formula": "count(invoices WHERE high_risk_flag = TRUE)",
    "where": "Portfolio Billing",
    "why": "Catches systemic billing errors before they accumulate into an audit finding.",
    "how": "Controller reviews flagged invoices in the weekly close; rejects until cleaned up.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-007b",
    "name": "Peer-Deviation Project Count",
    "type": "KRI",
    "category": "Billing",
    "summary": "Projects more than 1.5σ off peer-archetype median on % complete vs. % billed.",
    "formula": "count(projects WHERE distance from archetype median > 1.5σ on (pct_complete, pct_billed))",
    "where": "Portfolio Billing (peer scatter)",
    "why": "Peer-relative drift surfaces hidden margin or cash-flow drift not visible at the project level.",
    "how": "Operations + PM review outliers monthly; resolution is either CO update, EAC refresh, or pay-app catch-up.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-008",
    "name": "EAC Drift",
    "type": "KRI",
    "category": "Estimate quality",
    "summary": "EAC drift relative to original budget, percentage of original.",
    "formula": "(current_eac − original_eac) / original_eac",
    "where": "Portfolio Billing · NIF · Simulator",
    "why": "Direct measure of budget erosion; biggest single driver of forward-margin surprise.",
    "how": "PM owns; controller escalates positive drift > 5%; CFO sees portfolio aggregate weekly.",
    "impact": "High",
    "owner": "Controller/PM",
    "refresh": "Daily"
  },
  {
    "id": "K-009",
    "name": "Net Income (Current)",
    "type": "KPI",
    "category": "Profitability",
    "summary": "Net income for the current period, derived from GL aggregates.",
    "formula": "Σ revenue_postings − Σ expense_postings (current period)",
    "where": "Overview · NIF",
    "why": "Standard P&L baseline; reference for forecast-accuracy validation.",
    "how": "Standard reporting; used as the actual against which K-009b forecasts are scored.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-009b",
    "name": "Net Income Forecast (Rolling 12-24M)",
    "type": "KPI",
    "category": "Profitability/Forecast",
    "summary": "Rolling 12–24 month net income forecast with conformal PI.",
    "formula": "ML stack with ABI, PPI, 10Y, mix-covariates; 50/80/95 conformal PI from out-of-sample residuals",
    "where": "Net Income Forecast · Decision Pack",
    "why": "POC requires ±5% annual / <10% quarterly miss; banking covenant pack consumes lower PI bound.",
    "how": "CFO uses for guidance; Treasury sizes credit; CRO uses lower PI as covenant stress input.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily/Monthly"
  },
  {
    "id": "K-009c",
    "name": "Net Income Forecast Accuracy vs Actual",
    "type": "KRI",
    "category": "Model quality/Profitability",
    "summary": "Forecast accuracy on closed periods — actual vs. K-009b prediction.",
    "formula": "(actual_NI − forecast_NI) / actual_NI per closed period",
    "where": "Net Income Forecast",
    "why": "Calibration evidence; demonstrates whether the model meets the POC accuracy bar.",
    "how": "MLOps reviews monthly; out-of-bounds triggers retrain evaluation.",
    "impact": "Critical",
    "owner": "Vendor lead",
    "refresh": "Per close"
  },
  {
    "id": "K-009d",
    "name": "Forecast Accuracy by Branch/PM",
    "type": "KPI",
    "category": "Forecast",
    "summary": "K-009c decomposed by branch and PM to identify localised forecast bias.",
    "formula": "K-009c stratified by dim_branch and dim_employee_pm",
    "where": "Net Income Forecast",
    "why": "Catches systematic bias in specific branches or PMs that aggregate accuracy can mask.",
    "how": "Branch leads review monthly; persistent bias triggers feature engineering or retrain.",
    "impact": "High",
    "owner": "Vendor lead",
    "refresh": "Per close"
  },
  {
    "id": "K-010",
    "name": "Pending CO $ Exposure",
    "type": "KRI",
    "category": "Change orders",
    "summary": "Total dollar value of pending change orders not yet approved.",
    "formula": "Σ ChangeAmountRevenue WHERE Status = Pending",
    "where": "Portfolio Billing · Decision Pack",
    "why": "Pending CO is unbilled revenue exposure; high values predict cash-flow lumpiness.",
    "how": "PM chases approvals; controller treats > 5% of contract as a cash-flow risk in DP forecast.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-010b",
    "name": "CO Approval Lag (median days)",
    "type": "KRI",
    "category": "Change orders",
    "summary": "Median days from CO submitted to CO approved.",
    "formula": "median(approved_date − submitted_date) for COs in period",
    "where": "Portfolio Billing",
    "why": "Approval lag is a counterparty-relationship metric; widening lag predicts dispute risk.",
    "how": "Controller flags lag > 30 days; account manager engages owner's PM for resolution.",
    "impact": "High",
    "owner": "PM/Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-013",
    "name": "DSO by Tier",
    "type": "KPI",
    "category": "AR",
    "summary": "Days Sales Outstanding broken out by customer tier.",
    "formula": "weighted avg (issue_date → close_date) days, by tier",
    "where": "AR & Customer Risk",
    "why": "Tier-level DSO catches deterioration in a single tier that enterprise DSO would mask.",
    "how": "Credit manager reviews weekly; tier-specific DSO drift triggers credit-terms review or hold.",
    "impact": "Critical",
    "owner": "Treasury/Collections",
    "refresh": "Daily"
  },
  {
    "id": "K-014",
    "name": "DSO Drift Enterprise",
    "type": "KRI",
    "category": "AR",
    "summary": "Drift in enterprise rolling-30 DSO versus trailing-12 baseline.",
    "formula": "rolling_30_dso − trailing_12_baseline_dso",
    "where": "Overview · AR & Customer Risk",
    "why": "Headline AR-cycle metric; meets brief requirement for 30+ day early detection.",
    "how": "CFO board metric; controller and credit-ops act on positive drift > 5 days.",
    "impact": "Critical",
    "owner": "Treasury",
    "refresh": "Daily"
  },
  {
    "id": "K-014b",
    "name": "Operating Cash Flow",
    "type": "KPI",
    "category": "Cash",
    "summary": "Operating cash flow per period, derived from income plus working-capital changes.",
    "formula": "net_income + non_cash_items + Δ working_capital",
    "where": "NIF · Decision Pack",
    "why": "Cash-quality of earnings; gap between OCF and NI flags accrual or working-capital build.",
    "how": "Treasury watches monthly; widening gap triggers AR / inventory review.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily/Monthly"
  },
  {
    "id": "K-014c",
    "name": "Net Cash Position",
    "type": "KPI",
    "category": "Cash",
    "summary": "Net cash position — cash on hand minus short-term debt.",
    "formula": "Σ cash_balance − Σ short_term_debt",
    "where": "Overview · Decision Pack",
    "why": "Liquidity baseline; the line on which all forward cash forecasts pivot.",
    "how": "CFO and Treasury monitor daily; covenant tests evaluate against threshold.",
    "impact": "Critical",
    "owner": "Treasury/CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-015",
    "name": "Cash-Negative Probability (90/180d)",
    "type": "KRI",
    "category": "Cash/Risk",
    "summary": "Probability that net cash position goes negative in the next 90 / 180 days.",
    "formula": "P(cash_position_t < 0) at horizons 90 and 180 days from forecast distribution",
    "where": "Overview · NIF · Decision Pack",
    "why": "Translates cash forecast into a single tail-risk number for board and bank covenants.",
    "how": "CFO communicates to bank; Treasury draws on credit facility if probability > 5%.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-016",
    "name": "AR Aging (30/60/90/120+)",
    "type": "KPI",
    "category": "AR",
    "summary": "AR aging bucket distribution: 30 / 60 / 90 / 120+ days.",
    "formula": "Σ AR balance bucketed by days from due_date to as-of",
    "where": "AR & Customer Risk",
    "why": "Standard aging report; baseline against which K-027 drift is measured.",
    "how": "Credit manager reviews weekly; 90+ bucket growth triggers reserve adjustment and collections push.",
    "impact": "Critical",
    "owner": "Collections",
    "refresh": "Daily"
  },
  {
    "id": "K-017",
    "name": "Bad Debt Reserve",
    "type": "KPI",
    "category": "AR",
    "summary": "Bad-debt reserve estimate based on aging and historical write-off rates.",
    "formula": "Σ (aging_bucket_balance × historical_write_off_rate)",
    "where": "Overview · Decision Pack",
    "why": "Reserve calculation directly hits P&L; auditor's first stop on AR.",
    "how": "Controller sets quarterly; over-reserve hides margin, under-reserve shocks earnings.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-018",
    "name": "Concentration: Top-10 Customer Share of AR",
    "type": "KPI",
    "category": "Concentration",
    "summary": "Top-10 customers' share of total accounts-receivable balance.",
    "formula": "Σ (top-10 customer AR) / total AR",
    "where": "Overview · AR & Customer Risk",
    "why": "Concentration risk; one customer default can sink working capital if share is too high.",
    "how": "Sales leadership and CFO review monthly; > 50% triggers diversification strategy.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-019",
    "name": "Concentration: Top-10 Project Share of WIP",
    "type": "KPI",
    "category": "Concentration",
    "summary": "Top-10 projects' share of total work-in-progress.",
    "formula": "Σ (top-10 project WIP) / total WIP",
    "where": "Portfolio Billing",
    "why": "Project concentration; large project failure can blow forecast accuracy.",
    "how": "Operations and CFO use as a portfolio-rebalancing input for go / no-go decisions on next bid.",
    "impact": "Critical",
    "owner": "CFO/COO",
    "refresh": "Daily"
  },
  {
    "id": "K-021",
    "name": "Gross Margin (Enterprise)",
    "type": "KPI",
    "category": "Profitability",
    "summary": "Enterprise gross margin, percentage of revenue.",
    "formula": "(revenue − direct_cost) / revenue",
    "where": "Overview · NIF",
    "why": "Top of P&L; primary indicator of pricing power and cost control.",
    "how": "CFO communicates as core operating metric; PMs and estimators tracked against branch-level margin targets.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-022",
    "name": "Gross Margin Shrinkage MoM",
    "type": "KRI",
    "category": "Profitability",
    "summary": "Month-over-month gross margin shrinkage.",
    "formula": "K-021_t − K-021_t-1",
    "where": "Overview",
    "why": "Catches margin erosion at the earliest period grain before it becomes structural.",
    "how": "Controller flags MoM shrinkage > 50 bps; PMs explain in monthly review.",
    "impact": "Critical",
    "owner": "CFO/Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-022b",
    "name": "Gross Margin Shrinkage QoQ",
    "type": "KRI",
    "category": "Profitability",
    "summary": "Quarter-over-quarter gross margin shrinkage.",
    "formula": "K-021_q − K-021_q-1",
    "where": "Overview",
    "why": "Smoothed view of K-022 for board reporting.",
    "how": "Quarterly board metric; persistent shrinkage triggers strategic pricing review.",
    "impact": "Critical",
    "owner": "CFO",
    "refresh": "Quarterly"
  },
  {
    "id": "K-023",
    "name": "Backlog ($)",
    "type": "KPI",
    "category": "Pipeline",
    "summary": "Total dollar backlog from active and signed-but-not-started projects.",
    "formula": "Σ (current_contract_value − billed_to_date) for active + signed_unstarted",
    "where": "Overview",
    "why": "Forward revenue cover; bank covenant input.",
    "how": "Sales leadership and CFO use as new-business-velocity gauge; declining backlog triggers BD push.",
    "impact": "Critical",
    "owner": "CFO/COO",
    "refresh": "Daily"
  },
  {
    "id": "K-024",
    "name": "DSCR (Debt Service Coverage Ratio)",
    "type": "KPI",
    "category": "Solvency",
    "summary": "Debt Service Coverage Ratio — operating cash flow over debt service.",
    "formula": "(EBITDA − tax − unfunded_capex) / (interest + principal payments)",
    "where": "Overview · Decision Pack",
    "why": "Primary banking-covenant metric; below 1.25x typically triggers covenant breach.",
    "how": "CFO and bank watch monthly; persistent decline forces refinance or asset sale.",
    "impact": "Critical",
    "owner": "CFO/Treasury",
    "refresh": "Monthly"
  },
  {
    "id": "K-026",
    "name": "Margin Fade by % Complete Bucket",
    "type": "KRI",
    "category": "Profitability",
    "summary": "Margin fade by % complete bucket — how margin erodes across project life.",
    "formula": "(current_margin − bid_margin) stratified by pct_complete bucket (0-25, 25-50, 50-75, 75-100)",
    "where": "Portfolio Billing",
    "why": "Reveals where margin is lost in the project lifecycle (early scope vs late close-out).",
    "how": "Operations targets specific phases for cost-control improvements based on which bucket fades hardest.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-027",
    "name": "Customer Pay-Cycle Drift",
    "type": "KRI",
    "category": "AR/Customer",
    "summary": "Per-customer pay-cycle drift versus the customer's own 24-month baseline.",
    "formula": "current per-customer pay_cycle − trailing-24-month per-customer baseline",
    "where": "AR & Customer Risk · Customer 360",
    "why": "Customer-specific signal that fires before tier-level or enterprise-level DSO drift.",
    "how": "Credit manager pulls onto watchlist; account manager engages customer when drift > 7 days.",
    "impact": "Critical",
    "owner": "Collections",
    "refresh": "Daily"
  },
  {
    "id": "K-028",
    "name": "Customer Risk Tier",
    "type": "KRI",
    "category": "Customer/Risk",
    "summary": "Composite customer-distress tier: Critical / High / Medium / Low.",
    "formula": "Bucketed score from 11-feature additive model (see lib/scoring.ts)",
    "where": "AR & Customer Risk · Simulator · Customer 360",
    "why": "Single per-customer risk number used by ops, credit, and CFO; drives reserve calc.",
    "how": "Sales leadership reviews Critical-tier list weekly; credit-hold decisions pivot on this score.",
    "impact": "Critical",
    "owner": "CFO/Credit Committee",
    "refresh": "Daily"
  },
  {
    "id": "K-029",
    "name": "Pay-If-Paid Exposure",
    "type": "KPI",
    "category": "AR risk",
    "summary": "AR balance on customers / contracts flagged pay-if-paid.",
    "formula": "Σ AR balance WHERE customer_or_contract_pay_if_paid = TRUE",
    "where": "AR & Customer Risk",
    "why": "PIP terms shift insolvency risk back to contractor; the dollar exposure must be quantified.",
    "how": "Credit manager sizes reserve; CFO communicates to board as quasi-contingent liability.",
    "impact": "High",
    "owner": "Treasury",
    "refresh": "Daily"
  },
  {
    "id": "K-030",
    "name": "Disputed Invoice $",
    "type": "KPI",
    "category": "AR",
    "summary": "Total dollars of currently disputed invoices.",
    "formula": "Σ invoice_amount WHERE dispute_open = TRUE",
    "where": "AR & Customer Risk",
    "why": "Hard cash exposure with unknown timing; widening disputes predict pay-cycle stretch.",
    "how": "Account managers triage weekly; CFO reviews aged disputes > 60 days for write-down decision.",
    "impact": "Medium",
    "owner": "Collections",
    "refresh": "Daily"
  },
  {
    "id": "K-031",
    "name": "Retainage Outstanding ($)",
    "type": "KPI",
    "category": "AR/Cash",
    "summary": "Outstanding retainage receivable at as-of date.",
    "formula": "Σ ProjInvoiceJour.RetainAmount − Σ retainage_releases",
    "where": "Decision Pack",
    "why": "Long-cycle receivable; asymmetric — retainage release accelerates cash, retainage held delays.",
    "how": "Controller tracks per-project; release-date-past triggers escalation per K-050.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-032",
    "name": "Bid-to-EAC Margin Fade",
    "type": "KPI",
    "category": "Profitability",
    "summary": "Total margin fade from bid to current EAC across active portfolio.",
    "formula": "Σ (bid_margin − current_eac_margin) on active projects",
    "where": "Portfolio Billing",
    "why": "Aggregated margin erosion view; if persistent, bid practices need recalibration.",
    "how": "Estimating leadership reviews quarterly; recurring patterns drive bid-template updates.",
    "impact": "Medium",
    "owner": "Controller/PM",
    "refresh": "Daily"
  },
  {
    "id": "K-033",
    "name": "Days Payable Outstanding (DPO)",
    "type": "KPI",
    "category": "Working Capital",
    "summary": "Days Payable Outstanding — days from invoice receipt to payment.",
    "formula": "(trade_payables / cost_of_goods_sold) × 365",
    "where": "Decision Pack",
    "why": "Working-capital lever; longer DPO funds operations cheaply, but stretches supplier relationships.",
    "how": "CFO and Treasury balance against supplier health; sub-tier insolvency risk rises if DPO > 60 days.",
    "impact": "Medium",
    "owner": "Controller",
    "refresh": "Daily"
  },
  {
    "id": "K-033b",
    "name": "DPO Drift",
    "type": "KRI",
    "category": "Working Capital",
    "summary": "DPO change versus trailing-12 baseline.",
    "formula": "current_DPO − trailing_12_DPO",
    "where": "Decision Pack",
    "why": "DPO drift signals working-capital stretch or supplier-side pushback.",
    "how": "Treasury monitors; sustained drift > 5 days flags bank conversation if covenant-relevant.",
    "impact": "Medium",
    "owner": "Controller",
    "refresh": "Weekly"
  },
  {
    "id": "K-034",
    "name": "Cancellation Rate (projects)",
    "type": "KPI",
    "category": "Pipeline",
    "summary": "Project cancellation rate over rolling period.",
    "formula": "cancellations / (cancellations + completions) over rolling 12 months",
    "where": "Portfolio Billing",
    "why": "Cancellation = cost incurred without revenue; quality-of-pipeline indicator.",
    "how": "Operations reviews root causes quarterly; persistent rise triggers pre-bid qualification tightening.",
    "impact": "Medium",
    "owner": "COO",
    "refresh": "Monthly"
  },
  {
    "id": "K-035",
    "name": "% Complete Distribution",
    "type": "KPI",
    "category": "WIP",
    "summary": "Distribution of active projects across percent-complete buckets.",
    "formula": "Histogram of pct_complete on active portfolio",
    "where": "Portfolio Billing",
    "why": "Portfolio-level visibility on lifecycle distribution; identifies bottlenecks at certain phases.",
    "how": "Operations rebalances PM workload; concentration in 75-100 bucket flags closeout-team capacity.",
    "impact": "Medium",
    "owner": "COO",
    "refresh": "Daily"
  },
  {
    "id": "K-036",
    "name": "Geographic Concentration",
    "type": "KPI",
    "category": "Concentration",
    "summary": "Top-N regions' share of active backlog.",
    "formula": "Σ (top-N region backlog) / total backlog",
    "where": "Portfolio Billing",
    "why": "Geographic concentration; weather, regulatory, or labor-market shock to a region hurts portfolio.",
    "how": "Strategy + CFO use to assess geographic diversification; informs new-branch decisions.",
    "impact": "Medium",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-037",
    "name": "Branch Concentration",
    "type": "KPI",
    "category": "Concentration",
    "summary": "Top-N branches' share of active backlog.",
    "formula": "Σ (top-N branch backlog) / total backlog",
    "where": "Portfolio Billing",
    "why": "Branch concentration; one branch's PM departure or labor shortage can blow forecast.",
    "how": "Operations reviews; over-concentration triggers branch-leadership succession planning.",
    "impact": "Medium",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-039",
    "name": "New Awards ($)",
    "type": "KPI",
    "category": "Pipeline",
    "summary": "New project awards (dollar value) per period.",
    "formula": "Σ contract_value of projects signed in period",
    "where": "Overview · Portfolio Billing",
    "why": "Forward-revenue velocity; backlog replenishment driver.",
    "how": "Sales leadership tracks weekly; declining awards signal bid-pipeline or win-rate problem.",
    "impact": "High",
    "owner": "COO/Sales",
    "refresh": "Daily"
  },
  {
    "id": "K-040",
    "name": "Forecast Confidence-Interval Coverage",
    "type": "KRI",
    "category": "Model quality",
    "summary": "Out-of-sample coverage of conformal PI bands per forecast layer.",
    "formula": "fraction of held-out actuals inside the stated PI on closed periods",
    "where": "Net Income Forecast (validation)",
    "why": "POC mandate: 80% PI coverage in [75%, 85%]; this is the calibration receipt.",
    "how": "MLOps reviews monthly; out-of-bounds coverage triggers conformal recalibration.",
    "impact": "Critical",
    "owner": "Vendor lead",
    "refresh": "Weekly"
  },
  {
    "id": "K-041",
    "name": "CO % of Contract Value",
    "type": "KPI",
    "category": "Change orders",
    "summary": "Approved CO dollars as percent of original contract value.",
    "formula": "Σ approved_CO_amount / original_contract_value",
    "where": "Portfolio Billing",
    "why": "High CO % is either healthy scope expansion or process failure; depends on margin.",
    "how": "PM and operations review monthly; > 15% with margin fade triggers contract-management review.",
    "impact": "Medium",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-042",
    "name": "Operating Margin",
    "type": "KPI",
    "category": "Profitability",
    "summary": "Operating margin — operating income over revenue.",
    "formula": "(revenue − COGS − operating_expenses) / revenue",
    "where": "Overview",
    "why": "P&L health below gross margin; reflects overhead absorption and operating efficiency.",
    "how": "CFO and CEO monitor monthly; consistent decline triggers GOH budget review.",
    "impact": "Medium",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-043",
    "name": "EBITDA / EBITDA Margin",
    "type": "KPI",
    "category": "Profitability",
    "summary": "EBITDA in dollars and as percent of revenue.",
    "formula": "EBITDA = revenue − operating_costs (excl. interest, tax, D&A); margin = EBITDA / revenue",
    "where": "Overview · Decision Pack",
    "why": "Banking-covenant input; standard valuation metric for ESOP and M&A discussions.",
    "how": "CFO uses for covenant test, ESOP valuation, and lender meetings.",
    "impact": "Medium",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-044",
    "name": "Beneish M-Score",
    "type": "KRI",
    "category": "Earnings quality",
    "summary": "Beneish M-Score — earnings-manipulation likelihood from financial ratios.",
    "formula": "weighted combination of 8 financial-statement ratios; M > -1.78 flags potential manipulation",
    "where": "AR & Customer Risk",
    "why": "Cross-checks customer financials for accounting red flags before material exposure builds.",
    "how": "Credit committee uses on public-customer 10-K data; flags new credit applications.",
    "impact": "Medium",
    "owner": "Controller",
    "refresh": "Quarterly"
  },
  {
    "id": "K-045",
    "name": "Piotroski F-Score",
    "type": "KPI",
    "category": "Fundamental quality",
    "summary": "Piotroski F-Score (0-9) — fundamental strength composite from 9 binary indicators.",
    "formula": "Σ of 9 binary indicators on profitability, leverage, and operating efficiency",
    "where": "AR & Customer Risk",
    "why": "Simple, robust customer-financial-strength gauge; complements Altman-Z and Beneish.",
    "how": "Credit committee views during onboarding and annual review on public customers.",
    "impact": "Medium",
    "owner": "CFO",
    "refresh": "Annual"
  },
  {
    "id": "K-046",
    "name": "Customer Credit Score Drift (D&B Paydex)",
    "type": "KRI",
    "category": "Customer/External",
    "summary": "Drift in customer's D&B Paydex score versus 12-month customer baseline.",
    "formula": "current_paydex − trailing_12_baseline_paydex",
    "where": "AR & Customer Risk · Simulator",
    "why": "Direct B2B-credit signal; Paydex drop precedes lien filings by weeks-to-months.",
    "how": "Credit manager pulls onto watchlist on drop > 10 points; reviews credit terms.",
    "impact": "High",
    "owner": "Credit Committee",
    "refresh": "Daily"
  },
  {
    "id": "K-047",
    "name": "Lien Filings Against Customer (last 90d)",
    "type": "KRI",
    "category": "Customer/External",
    "summary": "Mechanic's lien filings against the customer in last 90 days.",
    "formula": "count(lien_events WHERE customer_match AND date > as-of − 90)",
    "where": "AR & Customer Risk · Simulator",
    "why": "Hard distress signal — peers are not getting paid; predicts our own AR aging.",
    "how": "Credit manager triggers review; > 2 liens in 90 days typically forces credit-hold.",
    "impact": "High",
    "owner": "Credit Committee",
    "refresh": "Daily"
  },
  {
    "id": "K-048",
    "name": "Lawsuit/Bankruptcy Filings (last 90d)",
    "type": "KRI",
    "category": "Customer/External",
    "summary": "Lawsuit and bankruptcy filings against the customer in last 90 days.",
    "formula": "count(court_events WHERE customer_match AND event_type IN (lawsuit, bankruptcy))",
    "where": "AR & Customer Risk · Simulator",
    "why": "Hardest distress signal; bankruptcy filing triggers immediate AR write-down evaluation.",
    "how": "Legal + credit committee acts within 24 hours; suspends new orders pending evaluation.",
    "impact": "High",
    "owner": "Credit Committee",
    "refresh": "Daily"
  },
  {
    "id": "K-049",
    "name": "Lien Waiver Status Gaps",
    "type": "KRI",
    "category": "Compliance",
    "summary": "Lien-waiver document gaps on active billings.",
    "formula": "count(billings WHERE lien_waiver_required AND lien_waiver_status != 'received')",
    "where": "Portfolio Billing",
    "why": "Process compliance — missing waivers create legal exposure and slow payment.",
    "how": "Controller chases weekly; PM cannot release pay-app to certifier without waivers.",
    "impact": "Medium",
    "owner": "Legal/PM",
    "refresh": "Monthly"
  },
  {
    "id": "K-050",
    "name": "Retainage Past Release Date",
    "type": "KRI",
    "category": "Cash",
    "summary": "Retainage past contractual release date but not yet released.",
    "formula": "Σ retainage_balance WHERE release_date < as-of AND released = FALSE",
    "where": "Decision Pack",
    "why": "Cash trapped past entitlement; aged retainage is a cash-flow lever and dispute trigger.",
    "how": "Controller chases weekly; > 60 days past release date escalates to legal.",
    "impact": "Medium",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-051",
    "name": "Certified Payroll Compliance",
    "type": "KRI",
    "category": "Compliance",
    "summary": "Certified payroll compliance percentage on prevailing-wage projects.",
    "formula": "(certified_compliant_periods / total_required_periods) × 100",
    "where": "Decision Pack (compliance)",
    "why": "Federal and prevailing-wage projects can withhold payment for non-compliance.",
    "how": "HR and controller monitor; < 100% triggers immediate corrective action.",
    "impact": "Medium",
    "owner": "HR/Payroll",
    "refresh": "Monthly"
  },
  {
    "id": "K-052",
    "name": "Forecast Bias (over/under)",
    "type": "KRI",
    "category": "Model",
    "summary": "Forecast bias — average direction of forecast error per layer.",
    "formula": "mean(actual − forecast) over closed periods",
    "where": "Net Income Forecast (validation)",
    "why": "Coverage can be correct while bias is non-zero; bias means systematic over- or under-forecast.",
    "how": "MLOps + finance team review monthly; persistent bias triggers feature engineering or retrain.",
    "impact": "Medium",
    "owner": "Vendor lead",
    "refresh": "Per close"
  },
  {
    "id": "K-053",
    "name": "AIA Architecture Billings Index Trend",
    "type": "KPI",
    "category": "External/Macro",
    "summary": "AIA Architecture Billings Index trend (signal aggregate).",
    "formula": "ABI level + 3-month change; ABI > 50 = expansion",
    "where": "Net Income Forecast · Industry tab",
    "why": "9-12 month leading indicator for non-residential construction spend.",
    "how": "Strategy and CFO use to anchor forward NI bias; confidence-interval calibration.",
    "impact": "High",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-054",
    "name": "BLS PPI Construction Inputs Trend",
    "type": "KPI",
    "category": "External/Cost",
    "summary": "BLS PPI — Construction Inputs trend (signal aggregate).",
    "formula": "PCU2362212362 level + 3-month change",
    "where": "Net Income Forecast · Portfolio Billing",
    "why": "Material-cost inflation; covariate on margin-fade and EAC drift forecasts.",
    "how": "Estimating uses for bid markup; controller uses for material-cost stress in NIF.",
    "impact": "High",
    "owner": "Procurement",
    "refresh": "Monthly"
  },
  {
    "id": "K-055",
    "name": "FRED 10Y Treasury Yield Trend",
    "type": "KPI",
    "category": "External/Rates",
    "summary": "FRED 10-Year Treasury yield trend (signal aggregate).",
    "formula": "DGS10 level + 3-month change",
    "where": "Net Income Forecast · Decision Pack",
    "why": "Discount rate for ESOP valuations; rate-shift covariate for commercial demand.",
    "how": "CFO uses for ESOP-share-price model; informs capital-allocation decisions.",
    "impact": "High",
    "owner": "Strategy/CFO",
    "refresh": "Daily"
  },
  {
    "id": "K-056",
    "name": "Dodge Construction Pipeline Index",
    "type": "KPI",
    "category": "External/Demand",
    "summary": "Dodge Construction Pipeline Index trend.",
    "formula": "DMI level + 3-month change",
    "where": "Net Income Forecast · Industry tab",
    "why": "Forward-pipeline visibility; predicts new-award flow 6-12 months out.",
    "how": "Sales leadership uses for capacity planning; CFO uses for revenue forecast bias.",
    "impact": "High",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-057",
    "name": "CRE Credit Conditions (SLOOS / H.8)",
    "type": "KPI",
    "category": "External/Credit",
    "summary": "CRE credit conditions — composite of SLOOS tightening and Fed H.8 lending.",
    "formula": "weighted composite of DRTSCLCC (SLOOS) and H.8 CRE-lending growth",
    "where": "Net Income Forecast · Industry tab",
    "why": "Owner-side credit availability; tightening predicts project-start slowdown.",
    "how": "Strategy uses to gate new-customer onboarding; CFO uses for forward-revenue bias.",
    "impact": "High",
    "owner": "Strategy/CFO",
    "refresh": "Quarterly"
  },
  {
    "id": "K-058",
    "name": "Material Cost Index (basket)",
    "type": "KPI",
    "category": "External/Cost",
    "summary": "Material cost basket index — weighted commodity-price aggregate.",
    "formula": "Weighted basket: 30% steel + 25% cement + 20% lumber + 15% copper + 10% other",
    "where": "Portfolio Billing · Net Income Forecast",
    "why": "Single material-cost gauge; simpler stakeholder communication than per-commodity.",
    "how": "CFO and operations review monthly; basket > +5% YoY triggers margin-protection review.",
    "impact": "Medium",
    "owner": "Procurement",
    "refresh": "Daily"
  },
  {
    "id": "K-060",
    "name": "DSO by Project Type",
    "type": "KPI",
    "category": "AR",
    "summary": "DSO broken out by project archetype.",
    "formula": "weighted avg pay_cycle_days, by project_type",
    "where": "AR & Customer Risk",
    "why": "Archetype-specific DSO catches federal-vs-private payment-pattern differences.",
    "how": "Operations + credit team uses for archetype-specific credit-terms policy.",
    "impact": "Medium",
    "owner": "Treasury",
    "refresh": "Weekly"
  },
  {
    "id": "K-061",
    "name": "Active Litigation Count (Customer)",
    "type": "KRI",
    "category": "Customer/External",
    "summary": "Active-litigation count against the customer.",
    "formula": "count(open court cases WHERE customer_match)",
    "where": "AR & Customer Risk",
    "why": "Forward distress signal; litigation predicts financial distress 60-90 days out.",
    "how": "Legal flags; credit team escalates if count rises rapidly.",
    "impact": "Medium",
    "owner": "Legal/Credit Committee",
    "refresh": "Daily"
  },
  {
    "id": "K-062",
    "name": "Operating Cash Flow Margin",
    "type": "KPI",
    "category": "Cash",
    "summary": "Operating cash flow margin — OCF as percent of revenue.",
    "formula": "K-014b / revenue",
    "where": "Decision Pack",
    "why": "Cash-quality of revenue; complements gross margin with a cash-not-accrual lens.",
    "how": "CFO compares with gross-margin; persistent gap triggers AR / inventory review.",
    "impact": "Medium",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-070",
    "name": "Working Capital Ratio",
    "type": "KPI",
    "category": "Liquidity",
    "summary": "Working capital ratio — current assets over current liabilities.",
    "formula": "current_assets / current_liabilities",
    "where": "Decision Pack",
    "why": "Banking-covenant baseline; >1.0 minimum, >1.5 healthy.",
    "how": "CFO monitors monthly; covenant tests evaluate against threshold.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-071",
    "name": "Quick Ratio",
    "type": "KPI",
    "category": "Liquidity",
    "summary": "Quick ratio — liquid current assets over current liabilities.",
    "formula": "(current_assets − inventory − WIP) / current_liabilities",
    "where": "Decision Pack",
    "why": "Strict liquidity test; excludes WIP because WIP-to-cash conversion is uncertain.",
    "how": "CFO and Treasury monitor; <0.8 prompts cash-conservation actions.",
    "impact": "High",
    "owner": "Controller",
    "refresh": "Monthly"
  },
  {
    "id": "K-072",
    "name": "Debt-to-Equity",
    "type": "KPI",
    "category": "Solvency",
    "summary": "Debt-to-equity ratio.",
    "formula": "total_debt / total_equity",
    "where": "Decision Pack",
    "why": "Capital-structure health; ESOP and bank both watch this.",
    "how": "CFO monitors monthly; covenant breach triggers capital-restructure conversation.",
    "impact": "High",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-073",
    "name": "Interest Coverage Ratio",
    "type": "KPI",
    "category": "Solvency",
    "summary": "Interest coverage ratio — operating income over interest expense.",
    "formula": "operating_income / interest_expense",
    "where": "Decision Pack",
    "why": "Income-side debt-service test; covenant stress metric.",
    "how": "CFO + bank monitor; <2.0 triggers covenant-monitoring escalation.",
    "impact": "High",
    "owner": "CFO",
    "refresh": "Monthly"
  },
  {
    "id": "K-074",
    "name": "Altman Z'' Score",
    "type": "KRI",
    "category": "Distress",
    "summary": "Altman Z'' Score — bankruptcy-prediction composite for private companies.",
    "formula": "6.56X1 + 3.26X2 + 6.72X3 + 1.05X4 (X = standard ratios)",
    "where": "AR & Customer Risk",
    "why": "Industry-standard distress prediction; complements Beneish and Piotroski.",
    "how": "Credit committee uses for new-customer credit decision and annual review.",
    "impact": "High",
    "owner": "CFO",
    "refresh": "Quarterly"
  },
  {
    "id": "K-075",
    "name": "Forecast MAPE/MASE",
    "type": "KRI",
    "category": "Model",
    "summary": "MAPE / MASE forecast accuracy per layer.",
    "formula": "MAPE = mean(|actual − forecast| / |actual|); MASE = MAE / naive-baseline-MAE",
    "where": "Net Income Forecast (validation)",
    "why": "Industry-standard forecast-quality metrics for board / external reporting.",
    "how": "MLOps publishes monthly; out-of-bounds triggers retrain investigation.",
    "impact": "High",
    "owner": "Vendor lead",
    "refresh": "Per evaluation"
  },
  {
    "id": "K-076",
    "name": "Drift Score (PSI top-k features)",
    "type": "KRI",
    "category": "Model",
    "summary": "Population Stability Index on top-k features per model layer.",
    "formula": "Σ (current_pct − baseline_pct) × ln(current_pct / baseline_pct), per feature",
    "where": "(MLOps — not user-facing)",
    "why": "Drift detection; high PSI on a feature means the world it learned no longer exists.",
    "how": "MLOps reviews weekly; PSI > 0.25 triggers model-evaluation gate.",
    "impact": "High",
    "owner": "Vendor lead",
    "refresh": "Daily"
  },
  {
    "id": "K-080",
    "name": "Labor Productivity",
    "type": "KPI",
    "category": "Operations",
    "summary": "Labor productivity — revenue per labor hour.",
    "formula": "revenue_period / labor_hours_period",
    "where": "Portfolio Billing",
    "why": "Operational productivity gauge; declining productivity at flat margin signals price compression.",
    "how": "Operations leadership tracks per-archetype; targets corrective action on lagging branches.",
    "impact": "Low",
    "owner": "Operations",
    "refresh": "Monthly"
  },
  {
    "id": "K-084",
    "name": "Projects per PM",
    "type": "KPI",
    "category": "Capacity",
    "summary": "Active project count per project manager.",
    "formula": "count(active projects WHERE pm_assigned = pm_id)",
    "where": "Portfolio Billing",
    "why": "PM workload; over-allocation correlates with EAC drift and CO approval lag.",
    "how": "Operations reviews monthly; overload (> 8 active) triggers reassignment.",
    "impact": "Low",
    "owner": "Operations",
    "refresh": "Monthly"
  },
  {
    "id": "K-086",
    "name": "Avg Project Size",
    "type": "KPI",
    "category": "Portfolio",
    "summary": "Average dollar size of active projects.",
    "formula": "Σ contract_value(active) / count(active)",
    "where": "Portfolio Billing",
    "why": "Portfolio-shape metric; growing average size shifts capacity, capital, and risk profile.",
    "how": "Strategy reviews quarterly; trend changes drive bid-criteria recalibration.",
    "impact": "Low",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-087",
    "name": "Active Project Count",
    "type": "KPI",
    "category": "Portfolio",
    "summary": "Count of active projects.",
    "formula": "count(projects WHERE status = active)",
    "where": "Overview",
    "why": "Volume gauge; complement to backlog and average size.",
    "how": "Standard reporting; CEO and CFO communicate as headline operational metric.",
    "impact": "Low",
    "owner": "COO",
    "refresh": "Daily"
  },
  {
    "id": "K-089",
    "name": "OSHA Citations (Customer/Sub)",
    "type": "KPI",
    "category": "External/Compliance",
    "summary": "OSHA citation count against a customer or sub-contractor.",
    "formula": "count(citations WHERE entity_match AND date > as-of − 365)",
    "where": "AR & Customer Risk · Simulator",
    "why": "Sub-tier risk indicator; citation pattern predicts operational instability.",
    "how": "Credit + safety teams use during sub-onboarding; cluster of citations triggers re-vetting.",
    "impact": "Low",
    "owner": "Risk",
    "refresh": "Annual"
  },
  {
    "id": "K-090",
    "name": "Mortgage Originations Index (MBA)",
    "type": "KPI",
    "category": "External/Leading",
    "summary": "MBA Commercial Mortgage Originations Index trend.",
    "formula": "MBA-CMOI level + change",
    "where": "Net Income Forecast · Industry tab",
    "why": "Forward CRE-demand signal; declines presage commercial construction slowdown.",
    "how": "Strategy uses for sector outlook; CFO uses as NIF covariate.",
    "impact": "Low",
    "owner": "Strategy",
    "refresh": "Quarterly"
  },
  {
    "id": "K-091",
    "name": "Federal Construction Spending Trend",
    "type": "KPI",
    "category": "External/Macro",
    "summary": "Federal construction spending trend (Census C30 federal).",
    "formula": "federal C30 series level + 3-month change",
    "where": "Net Income Forecast · Industry tab",
    "why": "Federal-channel revenue driver; BIL/IIJA dollars flow through this metric.",
    "how": "Strategy + sales leadership use for federal-pursuit prioritisation.",
    "impact": "Low",
    "owner": "Strategy",
    "refresh": "Monthly"
  },
  {
    "id": "K-092",
    "name": "SLOOS tightness (CRE)",
    "type": "KPI",
    "category": "External/Credit",
    "summary": "SLOOS CRE tightness — net % banks reporting tighter CRE standards.",
    "formula": "DRTSCLCC level + change",
    "where": "Industry tab",
    "why": "Owner-side credit availability; tightening predicts project-start slowdown.",
    "how": "Strategy and CFO use for forward-revenue bias and customer-onboarding rate.",
    "impact": "Low",
    "owner": "Strategy",
    "refresh": "Quarterly"
  }
];
